import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    updateDealAction,
    useDealsSelector,
    prepareDealSetupState,
    getVehicle,
    type SalesCheckinRootState
} from '@makemydeal/dr-sales-checkin-store';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { DealVersionChangeType } from '@makemydeal/dr-shared-bff-types';
import type { IDxgVehicle } from '@makemydeal/dr-shared-types';
import type { SCICommonConsumer } from '@makemydeal/dr-sales-checkin-types';
import type { CustomerCardSaveData } from '@makemydeal/dr-activities-customer-card';
import { mapToDxgVehicle } from '@makemydeal/dr-shared-ui-utils';
import { mapShopperToConsumer } from '@makemydeal/dr-sales-checkin-ui-utils';

/**
 * useDealSetupLaunchDeal — Handles deal update from the Deal Setup screen.
 *
 * Flow:
 *  1. Merges the customer override (the buyer the Deal Central card just saved) into checkedInCustomer
 *  2. Maps selected vehicle (IVehicle) to Meta format
 *  3. Dispatches updateDealAction to save the deal
 *  4. Uses the existing dealValidation state machine
 *     (DEAL_OPERATION_IN_PROGRESS → DEAL_OPERATION_COMPLETED) to detect completion
 *  5. Redirects based on mode:
 *     - Standard mode → Sales View (via buildShopperUrl)
 *     - Traditional mode → SCI flow (vehicleDetails)
 *
 * Mandatory fields by mode (validated in DealSetupFooter):
 *  - STANDARD:     dealership (always), a valid customer (enforced by the card), valid vehicle
 *  - TRADITIONAL:  dealership (always) — customer and vehicle are optional
 */
export const useDealSetupLaunchDeal = () => {
    const dispatch = useDispatch();
    const { selectedDxgVehicle: existingMeta } = useDealsSelector();
    // Inline actions apply the row selection to `state.salesCheckin.vehicle` immediately, so `getVehicle`
    // is current.
    const enableCorrectPhoneHandling = useSelector(featureToggleSelectors.isCorrectPhoneHandlingEnabled);
    const selectedVehicle = useSelector((state: SalesCheckinRootState) => getVehicle(state));

    const [isLaunching, setIsLaunching] = useState(false);

    /**
     * Launches the deal — always updates the existing deal with current state.
     *
     * @param customerOverride — buyer fields to merge into checkedInCustomer before the update.
     *   The Deal Central card owns the customer save; passing its reconciled buyer here keeps the
     *   updateDeal payload fresh (the shared updateDeal always sends customerInfo). Defaults to `{}`
     *   for the Traditional flow, where the customer is optional and nothing needs merging.
     */
    const launchDeal = useCallback(
        (customerOverride: Partial<SCICommonConsumer> = {}) => {
            setIsLaunching(true);

            // 1. Resolve vehicle meta — use the newly selected vehicle if available,
            //    otherwise fall back to the existing meta from bootstrap.
            //
            //    When user selects a new vehicle via ChangeVehicle, we map it to Meta format.
            //    Stock/inventory vehicles have vin+stockNumber; Build vehicles only have chromeStyleId.
            const hasSelectedVehicle = selectedVehicle?.vin || selectedVehicle?.chromeStyleId;
            const vehicleMeta = hasSelectedVehicle ? mapToDxgVehicle(selectedVehicle!) : existingMeta || ({} as IDxgVehicle);

            // 2. Pre-populate customer + vehicle meta into reducers.
            //    PREPARE_DEAL_SETUP_STATE is NOT caught by the middleware, so no
            //    API call fires here — it only seeds state for the next dispatch.
            dispatch(prepareDealSetupState(customerOverride, vehicleMeta));

            // 3. Now trigger the actual API call — the middleware's getState() will
            //    see the customer + meta set in step 2 (reducers have already run).
            //    Tagged DEAL_SETUP: without it, Traditional mode's first Launch derives changeType
            //    undefined, which blocks DXG eventing and stops aio-dxg from minting a dealRefId (DE763458).
            dispatch(updateDealAction(vehicleMeta, undefined, DealVersionChangeType.DEAL_SETUP));
        },
        [dispatch, selectedVehicle, existingMeta]
    );

    /**
     * handleCardSave — continues the launch once the Deal Central customer card saves.
     *
     * The card owns the customer save. When it succeeds, reconcile the buyer back into SCI's
     * consumer shape and continue the launch (vehicle + navigation) with fresh data.
     */
    const handleCardSave = useCallback(
        ({ buyer }: CustomerCardSaveData) => {
            launchDeal(mapShopperToConsumer(buyer, enableCorrectPhoneHandling));
        },
        [launchDeal, enableCorrectPhoneHandling]
    );

    return {
        launchDeal,
        handleCardSave,
        isLaunching
    };
};
